import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TopBar } from "@/components/layout/TopBar";
import { PROFILE } from "@/constants/testIds";
import { toast } from "sonner";
import { Sparkles, UserCheck } from "lucide-react";

const csv = (arr) => (arr || []).join(", ");
const parseCsv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export default function Profile() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({
    headline: "", summary: "", current_role: "", years_experience: 0,
    target_roles: "", target_locations: "", industries: "",
    languages: "", skills: "", linkedin: "",
  });
  const [diagnostic, setDiagnostic] = useState({ score: 0, summary: "" });
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [me, setMe] = useState(null);
  const [consentBusy, setConsentBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profile");
        setForm({
          headline: data.headline || "",
          summary: data.summary || "",
          current_role: data.current_role || "",
          years_experience: data.years_experience || 0,
          target_roles: csv(data.target_roles),
          target_locations: csv(data.target_locations),
          industries: csv(data.industries),
          languages: csv(data.languages),
          skills: (data.skills || []).map((s) => s.name).join(", "),
          linkedin: data.linkedin || "",
        });
        setDiagnostic({
          score: data.diagnostic_score || 0,
          summary: data.diagnostic_summary || "",
        });
      } catch {}
      try {
        const { data: meData } = await api.get("/auth/me");
        setMe(meData);
      } catch {}
    })();
  }, []);

  const toggleProShareConsent = async () => {
    if (!me?.advisor_pro_key) return;
    setConsentBusy(true);
    const next = !me.pro_share_consent;
    try {
      await api.post("/auth/me/pro-share-consent", { consent: next });
      setMe((m) => ({ ...m, pro_share_consent: next }));
      toast.success(next
        ? "Partage activé : votre accompagnateur peut voir votre diagnostic"
        : "Partage désactivé");
    } catch {
      toast.error("Modification impossible");
    } finally { setConsentBusy(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        headline: form.headline,
        summary: form.summary,
        current_role: form.current_role,
        years_experience: Number(form.years_experience) || 0,
        target_roles: parseCsv(form.target_roles),
        target_locations: parseCsv(form.target_locations),
        industries: parseCsv(form.industries),
        languages: parseCsv(form.languages),
        skills: parseCsv(form.skills).map((n) => ({ name: n, level: "intermediate" })),
        linkedin: form.linkedin,
      };
      const { data } = await api.put("/profile", payload);
      setDiagnostic({ score: data.diagnostic_score || 0, summary: data.diagnostic_summary || diagnostic.summary });
      toast.success(lang === "fr" ? "Profil enregistré" : "Profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const runDiag = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/profile/diagnostic");
      setDiagnostic({ score: data.diagnostic_score, summary: data.diagnostic_summary });
      toast.success(lang === "fr" ? "Diagnostic généré" : "Diagnostic ready");
    } catch {
      toast.error(lang === "fr" ? "Erreur du diagnostic" : "Diagnostic error");
    } finally { setBusy(false); }
  };

  const bind = (key) => ({
    value: form[key],
    onChange: (e) => setForm((f) => ({ ...f, [key]: e.target.value })),
    className: "w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2 text-sm text-[#1F2937]",
  });

  return (
    <div data-testid={PROFILE.root}>
      <TopBar title={t("profile.title")} subtitle={t("profile.subtitle")} />
      <div className="px-6 md:px-10 py-10 md:py-14 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
        <div className="lg:col-span-2 space-y-5">
          <Field label={t("profile.headline")}>
            <input data-testid={PROFILE.headline} {...bind("headline")} />
          </Field>
          <Field label={t("profile.summary")}>
            <textarea data-testid={PROFILE.summary} rows={4}
              {...bind("summary")}
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label={t("profile.current_role")}>
              <input data-testid={PROFILE.currentRole} {...bind("current_role")} />
            </Field>
            <Field label={t("profile.years")}>
              <input data-testid={PROFILE.years} type="number" min={0} max={70}
                {...bind("years_experience")}
              />
            </Field>
          </div>
          <Field label={t("profile.target_roles")}>
            <input data-testid={PROFILE.targetRoles} {...bind("target_roles")} />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label={t("profile.locations")}>
              <input data-testid={PROFILE.locations} {...bind("target_locations")} />
            </Field>
            <Field label={t("profile.industries")}>
              <input data-testid={PROFILE.industries} {...bind("industries")} />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label={t("profile.languages")}>
              <input data-testid={PROFILE.languages} {...bind("languages")} />
            </Field>
            <Field label={t("profile.linkedin")}>
              <input data-testid={PROFILE.linkedin} {...bind("linkedin")} />
            </Field>
          </div>
          <Field label={t("profile.skills")}>
            <input data-testid={PROFILE.skills} {...bind("skills")} />
          </Field>
          <div className="pt-4">
            <button
              data-testid={PROFILE.save}
              onClick={save} disabled={saving}
              className="rounded-md bg-[#0B2545] hover:bg-[#DCC29A] disabled:opacity-60 text-white font-medium px-6 py-2.5 transition-colors"
            >
              {saving ? t("common.loading") : t("profile.save")}
            </button>
          </div>
        </div>

        <aside className="space-y-4">
          <div className="rounded-md border border-[#E4E1D8] bg-white p-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#5B5B62] mb-3">
              {t("profile.diagnostic")}
            </div>
            <div className="flex items-baseline">
              <div className="font-mono text-5xl text-[#1F2937] tabular-nums">{diagnostic.score}</div>
              <div className="ml-1 text-[#5B5B62] font-mono">/100</div>
            </div>
            <div className="mt-3 h-1.5 bg-[#EBE8DE] rounded-full overflow-hidden">
              <div className="h-full bg-[#0B2545]" style={{ width: `${diagnostic.score}%` }} />
            </div>
            <button
              data-testid={PROFILE.runDiagnostic}
              onClick={runDiag}
              disabled={busy}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-md border border-[#0B2545]/40 hover:border-[#0B2545] text-[#0B2545] text-sm py-2 transition-colors disabled:opacity-60"
            >
              <Sparkles className="h-4 w-4" strokeWidth={1.5} />
              {busy ? t("common.loading") : t("profile.run")}
            </button>
            {diagnostic.summary && (
              <div data-testid={PROFILE.diagnosticResult} className="mt-4 text-xs text-[#5B5B62] leading-relaxed whitespace-pre-wrap">
                {diagnostic.summary}
              </div>
            )}
          </div>

          {me?.advisor_pro_key && (
            <div className="rounded-md border border-[#E4E1D8] bg-white p-6" data-testid="profile-pro-share-card">
              <div className="flex items-start gap-3">
                <UserCheck className="h-4 w-4 text-[#0B2545] mt-0.5" strokeWidth={1.8} />
                <div className="flex-1">
                  <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#5B5B62] mb-1">
                    Accompagnement professionnel
                  </div>
                  <div className="text-sm text-[#1F2937] font-medium">
                    Vous êtes accompagné par un professionnel ALTIORA
                  </div>
                  <p className="mt-2 text-xs text-[#5B5B62] leading-relaxed">
                    En activant le partage, votre accompagnateur peut consulter votre diagnostic IMES&trade;
                    (scores, forces, vulnérabilités, scénarios) pour mieux vous conseiller. Vous restez
                    maître de la décision et pouvez révoquer ce partage à tout moment.
                  </p>
                  <label className="mt-4 flex items-center gap-3 cursor-pointer group" data-testid="profile-pro-share-toggle-label">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={!!me.pro_share_consent}
                        onChange={toggleProShareConsent}
                        disabled={consentBusy}
                        className="sr-only peer"
                        data-testid="profile-pro-share-toggle"
                      />
                      <div className="h-5 w-9 rounded-full bg-[#E4E1D8] peer-checked:bg-[#0B2545] transition-colors" />
                      <div className="absolute left-0.5 top-0.5 h-4 w-4 bg-white rounded-full transition-transform peer-checked:translate-x-4 shadow" />
                    </div>
                    <span className="text-xs text-[#374151]">
                      {me.pro_share_consent ? "Partage activé" : "Partage désactivé"}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="rounded-md border border-[#E4E1D8] bg-white p-6" data-testid="profile-security-card">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#5B5B62] mb-3">
              Sécurité du compte
            </div>
            <div className="flex items-start gap-2">
              <div className="h-2 w-2 rounded-full bg-[#C5A880] mt-1.5 shrink-0" />
              <div>
                <div className="text-sm text-[#1F2937] font-medium">Double authentification (MFA)</div>
                <div className="mt-1 text-xs text-[#8B8B8F] leading-relaxed">
                  Bientôt disponible pour tous les rôles à privilège. L&apos;infrastructure est en place et sera activée après la V1.
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-[#EBE8DE]">
              <a href="/reset-password" className="text-xs font-mono uppercase tracking-widest text-[#0B2545] hover:underline">
                Changer mon mot de passe →
              </a>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62]">{label}</div>
      {children}
    </label>
  );
}
